import React, { useEffect, useState, useRef } from 'react';
import { UserIcon, ShieldIcon, BellIcon, CameraIcon, SaveIcon, EyeIcon, EyeOffIcon, CheckIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, CalendarIcon, LockIcon, KeyIcon, AlertTriangleIcon, GlobeIcon, Loader2Icon, SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { supabaseAdmin } from '../lib/supabase';
import { getGoogleApiKey, setGoogleApiKey, validateGoogleApiKey, hasGoogleApiKey } from '../lib/googleMaps';
type Tab = 'perfil' | 'seguridad' | 'notificaciones' | 'integraciones';
const AVATAR_KEY = 'ventastrack_avatar';
const NOTIFS_KEY = 'ventastrack_notifs';
const defaultNotifs = {
  visitasCompletadas: true,
  nuevosCobros: true,
  nuevasVentas: false,
  reportesDiarios: true,
  alertasEquipo: false,
  recordatoriosVisita: true
};
export type NotifPrefs = typeof defaultNotifs;
export function loadNotifPrefs(): NotifPrefs {
  try {
    const saved = localStorage.getItem(NOTIFS_KEY);
    if (saved) return {
      ...defaultNotifs,
      ...JSON.parse(saved)
    };
  } catch {}
  return defaultNotifs;
}
export function loadAvatarUrl(userId?: string): string {
  if (userId) {
    const userSpecific = localStorage.getItem(`ventastrack_avatar_${userId}`);
    if (userSpecific) return userSpecific;
  }
  // Don't fall back to global key — each user has their own avatar
  return '';
}
export function SettingsPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Permission helpers for settings sub-tabs
  const canSeePerfil = isAdmin || user?.permisos?.settings_perfil === true || user?.permisos?.settings_perfil === undefined;
  const canSeeSeguridad = isAdmin || user?.permisos?.settings_seguridad === true || user?.permisos?.settings_seguridad === undefined;
  const canSeeNotificaciones = isAdmin || user?.permisos?.settings_notificaciones === true || user?.permisos?.settings_notificaciones === undefined;
  const canSeeIntegraciones = isAdmin || user?.permisos?.settings_integraciones === true;
  const [activeTab, setActiveTab] = useState<Tab>('perfil');
  // Perfil
  const [avatarUrl, setAvatarUrl] = useState<string>(() => loadAvatarUrl(user?.id));
  const [nombre, setNombre] = useState(user?.nombre || '');
  // Cargar avatar desde Supabase al iniciar (para sincronizar entre navegadores)
  useEffect(() => {
    if (!user?.id) return;
    const loadFromSupabase = async () => {
      const {
        data
      } = await supabaseAdmin.from('profiles').select('avatar_url').eq('id', user.id).single();
      if (data?.avatar_url) {
        setAvatarUrl(data.avatar_url);
        localStorage.setItem(`ventastrack_avatar_${user.id}`, data.avatar_url);
        window.dispatchEvent(new Event('avatar-updated'));
      }
    };
    loadFromSupabase();
  }, [user?.id]);
  const [email, setEmail] = useState(user?.email || '');
  const [perfilSaved, setPerfilSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Seguridad
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  // Notificaciones
  const [notifs, setNotifs] = useState<NotifPrefs>(loadNotifPrefs);
  const [notifSaved, setNotifSaved] = useState(false);
  // Integraciones
  const [googleKey, setGoogleKey] = useState(getGoogleApiKey);
  const [googleKeyInput, setGoogleKeyInput] = useState(getGoogleApiKey);
  const [isValidatingKey, setIsValidatingKey] = useState(false);
  const [keyValidation, setKeyValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [integrationSaved, setIntegrationSaved] = useState(false);
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };
  const strengthColors = ['bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500'];
  const strengthLabels = ['Muy débil', 'Débil', 'Buena', 'Fuerte'];
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      const base64 = ev.target?.result as string;
      setAvatarUrl(base64);
      if (user?.id) {
        localStorage.setItem(`ventastrack_avatar_${user.id}`, base64);
        // Guardar en Supabase para sincronizar entre navegadores
        await supabaseAdmin.from('profiles').update({
          avatar_url: base64
        }).eq('id', user.id);
      }
      window.dispatchEvent(new Event('avatar-updated'));
    };
    reader.readAsDataURL(file);
  };
  const handleSavePerfil = () => {
    setPerfilSaved(true);
    setTimeout(() => setPerfilSaved(false), 2500);
  };
  const handleSavePassword = () => {
    setPasswordError('');
    if (!currentPassword) {
      setPasswordError('Ingresa tu contraseña actual');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    addActivityLog('Cambió contraseña', 'Actualización de contraseña');
    setPasswordSaved(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setPasswordSaved(false), 2500);
  };
  const handleToggleNotif = (key: keyof NotifPrefs) => {
    setNotifs(prev => {
      const updated = {
        ...prev,
        [key]: !prev[key]
      };
      localStorage.setItem(NOTIFS_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event('notifs-updated'));
      return updated;
    });
  };
  const handleSaveNotifs = () => {
    localStorage.setItem(NOTIFS_KEY, JSON.stringify(notifs));
    window.dispatchEvent(new Event('notifs-updated'));
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2500);
  };
  const handleValidateGoogleKey = async () => {
    if (!googleKeyInput.trim()) return;
    setIsValidatingKey(true);
    setKeyValidation('idle');
    try {
      const isValid = await validateGoogleApiKey(googleKeyInput.trim());
      setKeyValidation(isValid ? 'valid' : 'invalid');
    } catch {
      setKeyValidation('invalid');
    } finally {
      setIsValidatingKey(false);
    }
  };
  const handleSaveGoogleKey = () => {
    setGoogleApiKey(googleKeyInput.trim());
    setGoogleKey(googleKeyInput.trim());
    addActivityLog('Guardó API Key Google Maps', 'Integración Google Maps configurada');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const handleRemoveGoogleKey = () => {
    setGoogleApiKey('');
    setGoogleKey('');
    setGoogleKeyInput('');
    setKeyValidation('idle');
    addActivityLog('Eliminó API Key Google Maps', 'Integración Google Maps desconectada');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const notifItems: {
    key: keyof NotifPrefs;
    label: string;
    desc: string;
    icon: React.ReactNode;
    color: string;
  }[] = [{
    key: 'visitasCompletadas',
    label: 'Visitas completadas',
    desc: 'Cuando un vendedor completa una visita',
    icon: <MapPinIcon className="w-4 h-4" data-id="element-4493" data-mp="3mcjfws276a3t" />,
    color: 'bg-[#eef2fc] text-[#2952C4]'
  }, {
    key: 'nuevosCobros',
    label: 'Nuevos cobros',
    desc: 'Alertas de cobros registrados en el sistema',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-4494" data-mp="2on6pqcwh3y0h" />,
    color: 'bg-emerald-100 text-emerald-600'
  }, {
    key: 'nuevasVentas',
    label: 'Nuevas ventas',
    desc: 'Alertas de ventas registradas',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-4495" data-mp="2ykutfsc24fus" />,
    color: 'bg-blue-100 text-blue-600'
  }, {
    key: 'reportesDiarios',
    label: 'Reportes diarios',
    desc: 'Resumen diario de actividad del equipo',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-4496" data-mp="du3vcnk6p19a" />,
    color: 'bg-[#d5e0f7] text-[#1E3D96]'
  }, {
    key: 'alertasEquipo',
    label: 'Alertas del equipo',
    desc: 'Actividad y cambios del equipo comercial',
    icon: <UsersIcon className="w-4 h-4" data-id="element-4497" data-mp="qbeluw6k2p76" />,
    color: 'bg-amber-100 text-amber-600'
  }, {
    key: 'recordatoriosVisita',
    label: 'Recordatorios de visita',
    desc: 'Recordatorios de próximas visitas programadas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-4498" data-mp="144e0kvyikfdo" />,
    color: 'bg-rose-100 text-rose-600'
  }];
  const allTabs: {
    id: Tab;
    label: string;
    icon: React.ReactNode;
    visible: boolean;
  }[] = [{
    id: 'perfil',
    label: 'Perfil',
    icon: <UserIcon className="w-5 h-5" data-id="element-4499" data-mp="vahs2jy9v1gq" />,
    visible: canSeePerfil
  }, {
    id: 'seguridad',
    label: 'Seguridad',
    icon: <ShieldIcon className="w-5 h-5" data-id="element-4500" data-mp="1n407t6wa2xfw" />,
    visible: canSeeSeguridad
  }, {
    id: 'notificaciones',
    label: 'Notificaciones',
    icon: <BellIcon className="w-5 h-5" data-id="element-4501" data-mp="1rvrs4zouc6xl" />,
    visible: canSeeNotificaciones
  }, {
    id: 'integraciones',
    label: 'Integraciones',
    icon: <GlobeIcon className="w-5 h-5" data-id="element-4502" data-mp="3g3khg5k7bycb" />,
    visible: canSeeIntegraciones
  }];
  const tabs = allTabs.filter(t => t.visible);
  // Auto-select first visible tab if current is hidden
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);
  const activeNotifCount = Object.values(notifs).filter(Boolean).length;
  const pwdStrength = getPasswordStrength(newPassword);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-4503" data-mp="2ifaffqkf1al7">
      <PageHeader title="Configuración" subtitle="Ajustes de cuenta y preferencias del sistema" icon={<SettingsIcon className="w-6 h-6" data-id="element-4505" data-mp="2nen1wa7dkfn9" />} data-id="element-4504" data-mp="2iglh9kuaw89o" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-stagger-1" data-id="element-4506" data-mp="29e2jvg9aanut">
        {/* Sidebar */}
        <div className="md:col-span-1 space-y-4" data-id="element-4507" data-mp="2dgflptkwu1mb">
          {/* User card */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center" data-id="element-4508" data-mp="1urlwv578tdz3">
            <div className="relative inline-block mb-4" data-id="element-4509" data-mp="1pt2bb5wka479">
              <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-lg mx-auto" style={{
              background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
            }} data-id="element-4510" data-mp="1pun52tb1xj42">
                {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-4511" data-mp="2dowt0gxkvrab" /> : <span className="text-2xl font-bold text-white" data-id="element-4512" data-mp="2mi9wgct9qban">
                    {getInitials(nombre || user?.nombre || '??')}
                  </span>}
              </div>
              <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-xl flex items-center justify-center shadow-lg transition-colors" style={{
              backgroundColor: '#2952C4'
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-4513" data-mp="2whr42zs2yz99">
                <CameraIcon className="w-3.5 h-3.5 text-white" data-id="element-4514" data-mp="27ug4ccx4da9l" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} data-id="element-4515" data-mp="2ctj7tsxgb8fg" />
            </div>
            <p className="font-bold text-slate-900 font-heading" data-id="element-4516" data-mp="2lu7pqypytsxm">
              {user?.nombre}
            </p>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-4517" data-mp="mjzkkjy3xsf2">{user?.rolNombre}</p>
            <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-200" data-id="element-4518" data-mp="rhhv2errwfp6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4519" data-mp="f6gr7zwzpr4p" />
              Activo
            </span>
          </div>

          {/* Tab navigation */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4520" data-mp="a5awjwjrrtsj">
            {tabs.map((tab, idx) => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-5 py-4 text-sm font-medium transition-all ${idx < tabs.length - 1 ? 'border-b border-slate-100' : ''}`} style={activeTab === tab.id ? {
            backgroundColor: '#eef2fc',
            color: '#1E3D96',
            borderRight: '2px solid #2952C4'
          } : {}} onMouseEnter={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#f8fafc';
          }} onMouseLeave={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '';
          }} data-id="element-4521" data-mp="12wksw1qmbgt2" data-mpi={idx}>
                <span style={activeTab === tab.id ? {
              color: '#2952C4'
            } : {
              color: '#94a3b8'
            }} data-id="element-4522" data-mp="wfj6t6q5nwka">
                  {tab.icon}
                </span>
                <span className={activeTab === tab.id ? 'font-heading' : 'text-slate-600 font-heading'} data-id="element-4523" data-mp="1f4fyr3y2yczc">
                  {tab.label}
                </span>
                {tab.id === 'notificaciones' && <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{
              backgroundColor: '#eef2fc',
              color: '#2952C4'
            }} data-id="element-4524" data-mp="3sackc1h2h1mb">
                    {activeNotifCount}
                  </span>}
              </button>)}
          </div>
        </div>

        {/* Right content */}
        <div className="md:col-span-3" data-id="element-4525" data-mp="34mv0cs41n7tw">
          {/* PERFIL TAB */}
          {activeTab === 'perfil' && canSeePerfil && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4526" data-mp="3jckdzmtbp4b2">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-4527" data-mp="3ejr82vm4bpxe">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-4528" data-mp="2lvlnq9yz82y3">
                  Información del Perfil
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-4529" data-mp="3fluf3x7p4pxo">
                  Actualiza tu información personal y foto de perfil
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-4530" data-mp="3kennqw6acif1">
                <div className="flex items-center gap-6 p-5 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-id="element-4531" data-mp="3pc735zepz18k">
                  <div className="relative flex-shrink-0" data-id="element-4532" data-mp="2smpwbh40395a">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-md" style={{
                  background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
                }} data-id="element-4533" data-mp="2wmq96xbgbca8">
                      {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-4534" data-mp="gv2gxf7du7xr" /> : <span className="text-2xl font-bold text-white" data-id="element-4535" data-mp="26f29kj0gc3lf">
                          {getInitials(nombre || user?.nombre || '??')}
                        </span>}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-4536" data-mp="1rov1mx82m2yz">
                    <p className="text-sm font-semibold text-slate-900 mb-1" data-id="element-4537" data-mp="1rnthm392u654">
                      Foto de perfil
                    </p>
                    <p className="text-xs text-slate-500 mb-3" data-id="element-4538" data-mp="1inolwqd8ww6s">
                      JPG, PNG o GIF. Máximo 5MB. Se guarda automáticamente.
                    </p>
                    <div className="flex items-center gap-2" data-id="element-4539" data-mp="1dqemchw57458">
                      <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors shadow-sm" style={{
                    backgroundColor: '#2952C4'
                  }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-4540" data-mp="18qachapqp7nz">
                        <CameraIcon className="w-4 h-4" data-id="element-4541" data-mp="1t6vbokul49m9" />
                        Subir foto
                      </button>
                      {avatarUrl && <button onClick={async () => {
                    setAvatarUrl('');
                    if (user?.id) {
                      localStorage.removeItem(`ventastrack_avatar_${user.id}`);
                      await supabaseAdmin.from('profiles').update({
                        avatar_url: null
                      }).eq('id', user.id);
                    }
                    window.dispatchEvent(new Event('avatar-updated'));
                  }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors" data-id="element-4542" data-mp="1y44xzrmog55v">
                          Eliminar
                        </button>}
                    </div>
                  </div>
                </div>

                {isAdmin ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-4543" data-mp="1zlakfrorh3yk">
                    <Input label="Nombre Completo" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre completo" data-id="element-4544" data-mp="1upd59op8i3nd" />
                    <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" data-id="element-4545" data-mp="34qy76af8k7zh" />
                  </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-4546" data-mp="39jhkd6a0h13r">
                    <div data-id="element-4547" data-mp="2lrtyj2rlwg8c">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4548" data-mp="2lt5c1uaut2cn">
                        Nombre Completo
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-4549" data-mp="2uttfjv2mfkba">
                        <UserIcon className="w-4 h-4 text-slate-400" data-id="element-4550" data-mp="2pt5nr76kizg0" />
                        <span className="text-sm text-slate-600" data-id="element-4551" data-mp="a2t17ufi9cxa">
                          {user?.nombre}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-4552" data-mp="536l9d8aliks">
                          Solo admin
                        </span>
                      </div>
                    </div>
                    <div data-id="element-4553" data-mp="hndc1gtzi7aa">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4554" data-mp="hkhcxp6ehck9">
                        Correo Electrónico
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-4555" data-mp="8mymg59zm60z">
                        <span className="text-sm text-slate-600" data-id="element-4556" data-mp="wc014a9eydep">
                          {user?.email || '—'}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-4557" data-mp="mlybvf5bqoy7">
                          Solo admin
                        </span>
                      </div>
                    </div>
                  </div>}

                <div data-id="element-4558" data-mp="1f9awvkvu6ope">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4559" data-mp="1f917nzim8lkr">
                    Rol del Sistema
                  </label>
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-4560" data-mp="1txks3fo52qv8">
                    <ShieldIcon className="w-4 h-4 text-slate-400" data-id="element-4561" data-mp="1oyzr3e12c35q" />
                    <span className="text-sm text-slate-600" data-id="element-4562" data-mp="1jylh3qfwij52">
                      {user?.rolNombre}
                    </span>
                    <span className="ml-auto text-xs text-slate-400" data-id="element-4563" data-mp="1fw8c548zap9c">
                      No editable
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-4564" data-mp="1676vdbum03ub">
                {perfilSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-4565" data-mp="11613fg8rwklu">
                    <CheckIcon className="w-4 h-4" data-id="element-4566" data-mp="w7g1ncdvhsva" />
                    Cambios guardados
                  </div>}
                <Button onClick={handleSavePerfil} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-4568" data-mp="2yvy4h0s8zmq0" />} data-id="element-4567" data-mp="n82rsilu72a1">
                  Guardar Cambios
                </Button>
              </div>
            </div>}

          {/* SEGURIDAD TAB */}
          {activeTab === 'seguridad' && canSeeSeguridad && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4569" data-mp="37xfh296g569y">
              <div className="px-8 py-6 border-b border-slate-100 bg-slate-50" data-id="element-4570" data-mp="3cuxrk4043tk2">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-4571" data-mp="2g71ayz4ocy8u">
                  Seguridad de la Cuenta
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-4572" data-mp="2p64u233uddnc">
                  Actualiza tu contraseña para mantener tu cuenta segura
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-4573" data-mp="2ty65xnx3l2cu">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-4574" data-mp="1wi0nzghcyyk4">
                  <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4575" data-mp="21fsd62qmc6km" />
                  <div data-id="element-4576" data-mp="7x1v7dzbrxke">
                    <p className="text-sm font-semibold text-amber-800" data-id="element-4577" data-mp="7ymm8tmfkydi">
                      Recomendación de seguridad
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-4578" data-mp="gq555carhsaa">
                      Usa una contraseña de al menos 10 caracteres con letras,
                      números y símbolos.
                    </p>
                  </div>
                </div>

                <div className="space-y-5" data-id="element-4579" data-mp="qmrmvs7sk383">
                  <div data-id="element-4580" data-mp="lp87gozcxkek">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4581" data-mp="lq1775zwf9n7">
                      Contraseña Actual
                    </label>
                    <div className="relative" data-id="element-4582" data-mp="zmoikdslm2cn">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-4583" data-mp="voh11gqb8x0e">
                        <KeyIcon className="w-4 h-4" data-id="element-4584" data-mp="31hfcl9e7ymy7" />
                      </div>
                      <input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" style={{
                    '--tw-ring-color': 'rgba(41,82,196,0.2)',
                    '--tw-border-opacity': '1'
                  } as React.CSSProperties} data-id="element-4585" data-mp="2xfakwg7c00e7" />
                      <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-4586" data-mp="1unhu69x1zzwx">
                        {showCurrent ? <EyeOffIcon className="w-4 h-4" data-id="element-4587" data-mp="3tyixj19t1hm0" /> : <EyeIcon className="w-4 h-4" data-id="element-4588" data-mp="3rm70mk67ltvi" />}
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4589" data-mp="3ijfpklh8x04e">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4590" data-mp="3ikqs6hyyg1tx">
                      Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-4591" data-mp="f2z4hfruq41j">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-4592" data-mp="f2pcjmn8xmt7">
                        <LockIcon className="w-4 h-4" data-id="element-4593" data-mp="a4nu93xn9ris" />
                      </div>
                      <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" data-id="element-4594" data-mp="5cdxp520xt7j" />
                      <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-4595" data-mp="15d9ahqe09m6z">
                        {showNew ? <EyeOffIcon className="w-4 h-4" data-id="element-4596" data-mp="1jdaaz4qcyrlx" /> : <EyeIcon className="w-4 h-4" data-id="element-4597" data-mp="1oc3pn0hdsono" />}
                      </button>
                    </div>
                    {newPassword && <div className="mt-3" data-id="element-4598" data-mp="1t3lnvgs0sb4y">
                        <div className="flex gap-1.5 mb-1.5" data-id="element-4599" data-mp="1y2g72jtwg1pl">
                          {[0, 1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i < pwdStrength ? strengthColors[pwdStrength - 1] : 'bg-slate-200'}`} data-id="element-4600" data-mp="232160u92ka2b" />)}
                        </div>
                        <p className={`text-xs font-medium ${pwdStrength <= 1 ? 'text-rose-500' : pwdStrength === 2 ? 'text-amber-500' : pwdStrength === 3 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-4601" data-mp="2oboo6zhi08v5">
                          {strengthLabels[pwdStrength - 1] || 'Muy débil'}
                        </p>
                      </div>}
                  </div>

                  <div data-id="element-4602" data-mp="24m4jo18g1msx">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4603" data-mp="2jddf4qz122dz">
                      Confirmar Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-4604" data-mp="2ekbv64jyrcnj">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-4605" data-mp="1qvsqdz04hi07">
                        <LockIcon className="w-4 h-4" data-id="element-4606" data-mp="1ly96aigqub0c" />
                      </div>
                      <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" className={`w-full pl-10 pr-12 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white ${confirmPassword && newPassword ? confirmPassword === newPassword ? 'border-emerald-400' : 'border-rose-400' : 'border-slate-200'}`} data-id="element-4607" data-mp="1ex49z7zqu6d" />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-4608" data-mp="b5ib29xtj50d">
                        {showConfirm ? <EyeOffIcon className="w-4 h-4" data-id="element-4609" data-mp="30uu16qushwz8" /> : <EyeIcon className="w-4 h-4" data-id="element-4610" data-mp="1yxtt02w2g543" />}
                      </button>
                    </div>
                    {confirmPassword && newPassword && <p className={`mt-1.5 text-xs font-medium flex items-center gap-1 ${confirmPassword === newPassword ? 'text-emerald-600' : 'text-rose-500'}`} data-id="element-4611" data-mp="28uyiabl7kgp4">
                        {confirmPassword === newPassword ? <>
                            <CheckIcon className="w-3 h-3" data-id="element-4612" data-mp="1l5dv1gi0uxa1" />
                            Las contraseñas coinciden
                          </> : '✗ Las contraseñas no coinciden'}
                      </p>}
                  </div>
                </div>

                {passwordError && <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-4613" data-mp="11hofq8r9uphp">
                    <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-4614" data-mp="txcb5veeu26x" />
                    {passwordError}
                  </div>}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-4615" data-mp="kx7ikrdvl8ct">
                {passwordSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-4616" data-mp="fycrz39o2fi5">
                    <CheckIcon className="w-4 h-4" data-id="element-4617" data-mp="b0uf51sj3g4v" />
                    Contraseña actualizada
                  </div>}
                <Button onClick={handleSavePassword} leftIcon={<ShieldIcon className="w-4 h-4" data-id="element-4619" data-mp="17di7ox3hkwz" />} data-id="element-4618" data-mp="19gir2vviwfy">
                  Actualizar Contraseña
                </Button>
              </div>
            </div>}

          {/* NOTIFICACIONES TAB */}
          {activeTab === 'notificaciones' && canSeeNotificaciones && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4620" data-mp="3jhp6bjzyrc34">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-4621" data-mp="39u3e7995zh7v">
                <div className="flex items-center justify-between" data-id="element-4622" data-mp="34se1zqa7gxi7">
                  <div data-id="element-4623" data-mp="34uqhzxwekxsm">
                    <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-4624" data-mp="3si8hkflwt065">
                      Notificaciones
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-4625" data-mp="1am5pzp1dre7">
                      Controla qué alertas deseas recibir
                    </p>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-4626" data-mp="3nr01j0q2qgk0">
                    <BellIcon className="w-4 h-4" style={{
                  color: '#2952C4'
                }} data-id="element-4627" data-mp="faww71mfp3yu" />
                    <span className="text-sm font-bold text-slate-900" data-id="element-4628" data-mp="24rshx0anlps5">
                      {activeNotifCount}
                    </span>
                    <span className="text-xs text-slate-500" data-id="element-4629" data-mp="1zrmvbyj8qtgx">activas</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-3" data-id="element-4630" data-mp="29j0s3yjt5f6d">
                {notifItems.map(item => <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border transition-all" style={notifs[item.key] ? {
              borderColor: '#aac1ef',
              backgroundColor: 'rgba(238,242,252,0.5)'
            } : {
              borderColor: '#f1f5f9',
              backgroundColor: 'white'
            }} data-id="element-4631" data-mp="1ltg4v3gmfvra">
                    <div className="flex items-center gap-4" data-id="element-4632" data-mp="1ds3pcccxjse">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`} data-id="element-4633" data-mp="1fcyn32l34qs">
                        {item.icon}
                      </div>
                      <div data-id="element-4634" data-mp="fd1j49sqo9vb">
                        <p className="text-sm font-semibold text-slate-900" data-id="element-4635" data-mp="fecnaaq3jjmy">
                          {item.label}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-4636" data-mp="odg3n6xvpkxr">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0" data-id="element-4637" data-mp="t6isj1rpo67m">
                      <input type="checkbox" className="sr-only peer" checked={notifs[item.key]} onChange={() => handleToggleNotif(item.key)} data-id="element-4638" data-mp="132cetcv8fe2l" />
                      <div className="brand-toggle w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all shadow-inner" data-id="element-4639" data-mp="2sksqsoyqgwny" />
                    </label>
                  </div>)}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-4640" data-mp="36amg18e5hiy7">
                <p className="text-sm text-slate-500" data-id="element-4641" data-mp="31iuy1m928ljo">
                  <span className="font-semibold text-slate-700" data-id="element-4642" data-mp="2wl2z489zzz5z">
                    {activeNotifCount}
                  </span>{' '}
                  de {notifItems.length} notificaciones activas
                </p>
                <div className="flex items-center gap-3" data-id="element-4643" data-mp="3p98k8w4yrq6c">
                  {notifSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-4644" data-mp="1nbok2owayn8v">
                      <CheckIcon className="w-4 h-4" data-id="element-4645" data-mp="1sbt3onsiby3b" />
                      Guardado
                    </div>}
                  <Button onClick={handleSaveNotifs} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-4647" data-mp="1ikg9vqn5exun" />} data-id="element-4646" data-mp="1dllocgxsqv6u">
                    Guardar Preferencias
                  </Button>
                </div>
              </div>
            </div>}

          {/* INTEGRACIONES TAB */}
          {activeTab === 'integraciones' && canSeeIntegraciones && <div className="space-y-6" data-id="element-4648" data-mp="up3rxm806749">
              {/* Google Maps Card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4649" data-mp="znotpq2wkyut">
                <div className="px-8 py-6 border-b border-slate-100" style={{
              background: 'linear-gradient(90deg, #e8f5e9, #f1f8e9)'
            }} data-id="element-4650" data-mp="2p2ljrlffxq53">
                  <div className="flex items-center gap-4" data-id="element-4651" data-mp="idkz8vsw06lw">
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center" data-id="element-4652" data-mp="df9mac7apfzg">
                      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" data-id="element-4653" data-mp="s3wdpca75y71">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-4654" data-mp="nc4hom89v09i" />
                        <circle cx="12" cy="9" r="3" fill="white" data-id="element-4655" data-mp="w5hisbghp86o" />
                      </svg>
                    </div>
                    <div data-id="element-4656" data-mp="1b0nu9dyctz7e">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 font-heading" data-id="element-4657" data-mp="1azw62o7373r5">
                        Google Maps Platform
                        {googleKey && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full" data-id="element-4658" data-mp="3cvg1044zjjmx">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4659" data-mp="2m1uzcqz1a008" />
                            Conectado
                          </span>}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-4660" data-mp="2vrxtiuhw5k05">
                        Mapas precisos, autocomplete de direcciones y GPS en
                        República Dominicana
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6" data-id="element-4661" data-mp="280ilqm7ciahi">
                  {/* Benefits */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-4662" data-mp="2d0n28c89757q">
                    {[{
                  icon: '🗺️',
                  label: 'Mapas actualizados RD',
                  desc: 'Los más precisos disponibles'
                }, {
                  icon: '🔍',
                  label: 'Autocomplete',
                  desc: 'Búsqueda moderna de direcciones'
                }, {
                  icon: '📍',
                  label: 'GPS preciso',
                  desc: 'Ubicación exacta de farmacias'
                }, {
                  icon: '🧭',
                  label: 'Detección de llegada',
                  desc: 'Verificar visitas automáticamente'
                }].map(b => <div key={b.label} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl" data-id="element-4663" data-mp="t368i4opqnaa">
                        <span className="text-lg" data-id="element-4664" data-mp="k3sxh7kxxqng">{b.icon}</span>
                        <div data-id="element-4665" data-mp="k5m6uumxuoz5">
                          <p className="text-sm font-semibold text-slate-900" data-id="element-4666" data-mp="2jgdh3pjcljeb">
                            {b.label}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-4667" data-mp="2ei3nhjd9uqut">{b.desc}</p>
                        </div>
                      </div>)}
                  </div>

                  {/* API Key Input */}
                  <div data-id="element-4668" data-mp="1nntwwq8hxsig">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4669" data-mp="1imo0ahbptl3n">
                      API Key de Google Maps
                    </label>
                    <div className="flex gap-2" data-id="element-4670" data-mp="1emvrc3ietcxr">
                      <div className="relative flex-1" data-id="element-4671" data-mp="2bbv0dkqtiir0">
                        <KeyIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4672" data-mp="2bak0vx4enx5p" />
                        <input type="password" value={googleKeyInput} onChange={e => {
                      setGoogleKeyInput(e.target.value);
                      setKeyValidation('idle');
                    }} placeholder="AIzaSy..." className={`w-full pl-10 pr-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white font-mono ${keyValidation === 'valid' ? 'border-emerald-400 ring-emerald-200' : keyValidation === 'invalid' ? 'border-rose-400 ring-rose-200' : 'border-slate-200'}`} data-id="element-4673" data-mp="26el35ix6t2sm" />
                      </div>
                      <Button variant="secondary" onClick={handleValidateGoogleKey} disabled={!googleKeyInput.trim() || isValidatingKey} data-id="element-4674" data-mp="pshube5f46e9">
                        {isValidatingKey ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-4675" data-mp="14he2rcbdzlfe" /> : 'Verificar'}
                      </Button>
                    </div>
                    {keyValidation === 'valid' && <p className="mt-2 text-sm text-emerald-600 font-medium flex items-center gap-1" data-id="element-4676" data-mp="1dfzh0v9tsead">
                        <CheckIcon className="w-4 h-4" data-id="element-4677" data-mp="3ek1mcbawx2g3" /> API Key válida —
                        Google Maps está listo
                      </p>}
                    {keyValidation === 'invalid' && <p className="mt-2 text-sm text-rose-600 font-medium flex items-center gap-1" data-id="element-4678" data-mp="3oad4b1rflc5z">
                        <AlertTriangleIcon className="w-4 h-4" data-id="element-4679" data-mp="3jimnccxznq5l" /> API Key
                        inválida — Verifica que esté correcta y tenga los APIs
                        habilitados
                      </p>}
                  </div>

                  {/* Instructions */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl" data-id="element-4680" data-mp="b3ut6yq4inlk">
                    <p className="text-sm font-semibold text-blue-800 mb-2" data-id="element-4681" data-mp="64hxrpl6s87d">
                      ¿Cómo obtener tu API Key?
                    </p>
                    <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside" data-id="element-4682" data-mp="2cv7xpmo6ith3">
                      <li data-id="element-4683" data-mp="27x63q6p7t9qu">
                        Ve a{' '}
                        <a href="https://console.cloud.google.com/apis" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-900" data-id="element-4684" data-mp="nd1010wd1tll">
                          Google Cloud Console
                        </a>
                      </li>
                      <li data-id="element-4685" data-mp="4mjk5wwmmshn">Crea un proyecto nuevo o selecciona uno existente</li>
                      <li data-id="element-4686" data-mp="9m5zcbw0mit3">
                        Habilita estos APIs:{' '}
                        <strong data-id="element-4687" data-mp="9k2zl0529bb6">Maps JavaScript API</strong>,{' '}
                        <strong data-id="element-4688" data-mp="3qvktg6qrz5hz">Places API</strong>,{' '}
                        <strong data-id="element-4689" data-mp="3vt47b5jk9g9e">Geocoding API</strong>
                      </li>
                      <li data-id="element-4690" data-mp="3vv77ujicarsd">
                        Ve a "Credenciales" → "Crear credenciales" → "API Key"
                      </li>
                      <li data-id="element-4691" data-mp="1u2iyrtj0la4o">Copia la API Key y pégala aquí</li>
                    </ol>
                  </div>
                </div>

                <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-4692" data-mp="3q3ukpx78zc8a">
                  <div data-id="element-4693" data-mp="3m4bwarg7wj0p">
                    {googleKey && <button onClick={handleRemoveGoogleKey} className="text-sm text-rose-600 hover:text-rose-700 font-medium hover:underline" data-id="element-4694" data-mp="3h3o5o6vwi470">
                        Desconectar Google Maps
                      </button>}
                  </div>
                  <div className="flex items-center gap-3" data-id="element-4695" data-mp="32e9v4vgl9gq1">
                    {integrationSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-4696" data-mp="2ybvgknjyd34k">
                        <CheckIcon className="w-4 h-4" data-id="element-4697" data-mp="2td20cnda6y0p" />
                        Guardado
                      </div>}
                    <Button onClick={handleSaveGoogleKey} disabled={!googleKeyInput.trim()} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-4699" data-mp="29cin11fwa6ga" />} data-id="element-4698" data-mp="2jg76ihrydlsr">
                      Guardar API Key
                    </Button>
                  </div>
                </div>
              </div>

              {/* Status info */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-id="element-4700" data-mp="2o9rjg9k220ev">
                <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-4701" data-mp="1qmiziizpczez">
                  Estado de Integraciones
                </h4>
                <div className="space-y-3" data-id="element-4702" data-mp="1vlvlz2mnw4h1">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-4703" data-mp="20kgo57lh4y84">
                    <div className="flex items-center gap-3" data-id="element-4704" data-mp="25b6t4jpzmz2a">
                      <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center" data-id="element-4705" data-mp="25cqbqgz49m78">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" data-id="element-4706" data-mp="17tovul7j4tq2">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-4707" data-mp="14slm6dbnn3za" />
                          <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-4708" data-mp="1ej7x3u8ff6bn" />
                        </svg>
                      </div>
                      <div data-id="element-4709" data-mp="lvc7f9h9yeod">
                        <p className="text-sm font-medium text-slate-900" data-id="element-4710" data-mp="luiwrxf7350y">
                          Google Maps
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-4711" data-mp="gy2a77nn8ypz">
                          Mapas, Places, Geocoding
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${googleKey ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-4712" data-mp="qt3unq6z5cm8">
                      <span className={`w-1.5 h-1.5 rounded-full ${googleKey ? 'bg-emerald-500' : 'bg-slate-400'}`} data-id="element-4713" data-mp="29rib0zmn9w0p" />
                      {googleKey ? 'Activo' : 'No configurado'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-4714" data-mp="24v1m437mfdmk">
                    <div className="flex items-center gap-3" data-id="element-4715" data-mp="2skm6mqjfaixy">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-4716" data-mp="2xinzu4akbyn5">
                        <MapPinIcon className="w-4 h-4 text-blue-600" data-id="element-4717" data-mp="2jr991baeyt5j" />
                      </div>
                      <div data-id="element-4718" data-mp="2nsumoyurvbgs">
                        <p className="text-sm font-medium text-slate-900" data-id="element-4719" data-mp="2ns2wy4nuw7yf">
                          Photon + OSM
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-4720" data-mp="3bgc9efz6jjcw">
                          Búsqueda gratuita (fallback)
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-4721" data-mp="2rklyyzkw0of2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4722" data-mp="2hthke0z4j5kr" />
                      Siempre activo
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}